import React, { useState, useEffect, useMemo } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import QuickPickGamesComing from "../../QuickPickGames/CardPieces/QuickPickGamesComing";
import { DateTime } from "luxon";
import SuperPickemOnePickCard from "../SuperPickemComponents/SuperPickemOnePickCard";
import SuperPickemSubmitModal from "../SuperPickemComponents/SuperPickemSubmitModal";
import { useSaveSuperPickemPicks } from "../../../hooks/pools/useSaveSuperPickemPicks";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../../redux/actions/loginImport";
import ResponseModal from "../../GameModals/ResponseModal/ResponseModal";
import MyPicksDateNavBar from "../../NavigationBars/MyPicksNavigation/MyPicksDateNavBar";
import MyPickSingleGameDisplay from "./MyPickSingleGameDisplay";

const MyPicks = () => {
    const {
        masterPoolData,
        intervalInfo,
        interval,
        pickState,
        league,
        myUserId,
        masterPoolId,
        pickIndex,
    } = useUserAndGameContext();

    const {
        gameSchedule,
        gameOdds,
        teams,
        currentInterval,
        intervalOptions,
        todaysInterval,
    } = useSuperPickemContext();
    //console.log("IntervalInfo:", intervalInfo);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const dispatch = useDispatch();

    const [submitPickModalOpen, setSubmitPickModalOpen] = useState(false);
    const [submittedPickInfo, setSubmittedPickInfo] = useState(null);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { mutate: saveSuperPickemPicks } = useSaveSuperPickemPicks();

    const myPicksModalInfo = masterPoolData?.modalInfo?.myPicks;

    const filteredSchedule = useMemo(() => {
        // filter the game schedule to where Day key matches interval AND Status !== "NotNecessary"
        return gameSchedule?.filter((game) => {
            return (
                game?.Day === interval &&
                game?.Status !== "NotNecessary"
            );
        }
        );
    }, [gameSchedule, interval]);
    //console.log("Filtered Schedule:", filteredSchedule);

    const filteredOdds = useMemo(() => {
        // filter the game odds to where Day key matches interval AND Status !== "NotNecessary"
        return gameOdds?.filter((game) => {
            return (
                game?.Day === interval &&
                game?.Status !== "NotNecessary"
            );
        }
        );
    }, [gameOdds, interval]);

    //console.log("Filtered Odds:", filteredOdds);

    const enhancedSchedule = useMemo(() => {
        if (!filteredSchedule || !filteredOdds) return [];

        // Sort filteredSchedule by DateTime (eaerliest first)
        filteredSchedule?.sort((a, b) => {
            const dateA = DateTime.fromISO(a.DateTime, { zone: "America/New_York" });
            const dateB = DateTime.fromISO(b.DateTime, { zone: "America/New_York" });
            return dateA - dateB;
        });
    
        return filteredSchedule?.map((scheduleGame) => {
            const matchingOdds = filteredOdds.find(oddsGame => 
                oddsGame.GlobalGameId === scheduleGame.GlobalGameID
            );
                //console.log("Matching Odds:", matchingOdds);
            if (matchingOdds) {
                // Find DraftKings odds first
                let pregameOdds = matchingOdds.PregameOdds?.find(
                    odds => odds.Sportsbook === 'DraftKings' && odds.Unlisted === null
                );
                //console.log("Pregame Odds:", pregameOdds);
                // Fall back to FanDuel if DraftKings not found
                if (!pregameOdds) {
                    pregameOdds = matchingOdds.PregameOdds?.find(
                        odds => odds.Sportsbook === 'FanDuel' && odds.Unlisted === null
                    );
                }
    
                const HomePointSpread = pregameOdds?.HomePointSpread;
                const OverUnder = pregameOdds?.OverUnder;
                
                return {
                    ...scheduleGame,
                    homeSpread: HomePointSpread,
                    total: OverUnder,
                };
            }
    
            return scheduleGame;
        });
    }, [filteredSchedule, filteredOdds]);
    
    //console.log("Enhanced Schedule:", enhancedSchedule);

    const timeNowInNewYork = DateTime.now().setZone("America/New_York");

    // Are all of enhancedSchedule's games in the past?
    const allGamesInPast = useMemo(() => {
        return enhancedSchedule?.every((game) => {
            const gameDateTime = DateTime.fromISO(game.DateTime, { zone: "America/New_York" });
            return gameDateTime < timeNowInNewYork;
        });
    }, [enhancedSchedule, timeNowInNewYork]);
    //console.log("All Games In Past:", allGamesInPast);
    

    const thisIntervalsPick = useMemo(() => {
        return pickState?.find((pick) => pick?.interval === interval);
    }, [pickState, interval]);
    

    const handleSubmitPick = () => {
        //console.log("Submitting pick...");  
        const pickData = {
            myUserId: myUserId,
            masterPoolId: masterPoolId,
            pickIndex: Number(pickIndex),
            pick: submittedPickInfo,
            interval: interval,
        }
        //console.log("Pick Data:", pickData);

        try {
            saveSuperPickemPicks(pickData, {
                onSuccess: (data) => {
                    //console.log("Pick saved successfully:", data);
                    setSubmitPickModalOpen(false);
                    setSuccessModalOpen(true);
                },
                onError: (error) => {
                    console.error('Error submitting picks:', error);
                    
                    // Check for token expiration (401 Unauthorized)
                    if (error?.response?.status === 401) {
                        dispatch(userLogoutAction({
                            logoutType: 'token-expired'
                        }));
                        return;
                    }
                    
                    const errorMessage = error?.response?.data?.message || error?.response?.data?.checkerData?.message || 'An error occurred while submitting your picks. Please try again.';
                    setErrorMessage(errorMessage);
                    setErrorModalOpen(true);
                    setSubmitPickModalOpen(false);
                    setSubmittedPickInfo(null);
                }
            });
        } catch (error) {
            console.error("Error in handleSubmitPick:", error);
            setSubmitPickModalOpen(false);
            setSubmittedPickInfo(null);
            setErrorMessage("An unexpected error occurred");
            setErrorModalOpen(true);
        }
    };

    const handleCancelPick = () => {
        //console.log("Cancelling pick...");
        setSubmittedPickInfo(null);
        setSubmitPickModalOpen(false);
    };


    return (
        <>
            {myPicksModalInfo && (
                <>
                    <QuickPickGamesComing
                        page={'my-picks'}
                        title={myPicksModalInfo?.title}
                        body={myPicksModalInfo?.body}
                        quickPick={false}
                    />

                    <Box sx={{ marginBottom: '48px' }} /> 
                </>  
            )}

            {!myPicksModalInfo && (
                <>
                    <MyPickSingleGameDisplay 
                        pick={thisIntervalsPick} 
                        schedule={enhancedSchedule} 
                        teams={teams}
                        league={league}
                        todaysInterval={todaysInterval}
                        interval={interval}
                        allGamesInPast={allGamesInPast}
                    />
                   
                    <MyPicksDateNavBar theCurrentInterval={currentInterval} intervalOptions={intervalOptions} />
                    
                    <Box
                        sx={{
                            maxWidth: isMobile ? '300px' : '500px',
                            margin: '0 auto',
                            marginTop: '16px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: isMobile ? '12px' : '16px',
                                fontWeight: '700'
                            }}
                        >
                            Select one team to win against the spread. <br/> All picks are final and cannot be changed.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: 2 }}>
                        {enhancedSchedule?.length > 0 && (
                            enhancedSchedule.map((game, index) => (
                                <SuperPickemOnePickCard
                                    key={index}
                                    game={game}
                                    timeNowInNewYork={timeNowInNewYork}
                                    thisIntervalsPick={thisIntervalsPick}
                                    setSubmitPickModalOpen={setSubmitPickModalOpen}
                                    setSubmittedPickInfo={setSubmittedPickInfo}
                                />
                            ))
                        )}
                    </Box>
                </>
            )}

            {submitPickModalOpen && (
                <SuperPickemSubmitModal
                    open={submitPickModalOpen}
                    onClose={handleCancelPick}
                    handleSubmitPick={handleSubmitPick}
                    submittedPickInfo={submittedPickInfo}
                    league={league}
                    teams={teams}
                    schedule={enhancedSchedule}
                />
            )}

            {successModalOpen && (
                <SuperPickemSubmitModal
                    open={successModalOpen}
                    onClose={() => window.location.reload()} 
                    handleSubmitPick={handleSubmitPick}
                    submittedPickInfo={submittedPickInfo}
                    league={league}
                    teams={teams}
                    schedule={enhancedSchedule}
                    success={true}
                />
            )}


            {errorModalOpen && (
                <ResponseModal
                    open={errorModalOpen}
                    onClose={() => setErrorModalOpen(false)}
                    status="error"
                    message={errorMessage}
                />
            )}
        </>
    );
};

export default MyPicks;